// The snippet controller's "Create in ATS" half — `onCreateInATS`, `onCreateInAtsFormSent` /
// `onUpdateInAtsFormSent`, `logPendingOperation`, `onCreateInAtsWaitingModal`,
// `onOpenCreatedClientContactInAts`, `initLastCreatedClientContact`, `handlePendingOperationsOnInit`,
// `handleCompletedOperationByType` and the two completion callbacks — as one hook, so the route that
// renders `ClientContactSnippet` gets the button's state and the two modals from one place.
//
// ⚠️ IT RUNS WHETHER OR NOT THE BUTTON IS SHOWN. Upstream's `$onInit` calls
// `initLastCreatedClientContact` and `handlePendingOperationsOnInit` unconditionally — before and
// regardless of `isCreateClientContactEnabled` — so a profile on a tenant with the feature off still
// asks for the tracking row and still subscribes to a pending create. Both are reads.
//
// ⚠️ THE WRITE IS `createInAts`, AND IT REACHES THE CUSTOMER'S ATS. It is sent from exactly one
// place (`sendForm`), after the form's own checks and the server's read-only validation have
// passed, and it is not awaited by the press: upstream calls `onCreateInAtsFormSent(params)` and
// `scope.close()` on the next line, so the form is gone before the request answers.
import { useCallback, useEffect, useRef, useState, type ReactElement } from 'react'
import {
  AddAtsClientContactContext,
  ClientContactAsyncOperationType,
  createInAts as createInAtsApi,
  getLastAddedAtsClientTracking as getLastAddedAtsClientTrackingApi,
  type AddAtsClientContactParams,
  type ClientContactAddAtsCompletedOperationResult,
  type ClientContactAsyncMessageResponse,
  type ClientContactCompletedOperation,
  type AddAtsClientContactTracking,
} from '@woo-ext/api'
import type { ClientContactAsyncContainer } from './clientContactAsyncOperations.ts'
import {
  companiesAddedByWoo,
  createdContactGoToAtsCopy,
  disabledCreateInAtsTooltip,
  isCreateInAtsButtonDisabled,
  type CreateInAtsContact,
  type CreateInAtsState,
} from './createClientContact.ts'
import { CreateClientContactModal, type CreateClientContactModalProps } from './CreateClientContactModal.tsx'
import { CreateClientContactWaitingModal } from './CreateClientContactWaitingModal.tsx'

/**
 * The four analytics events, sent by the caller (the route owns the tracking context).
 *
 * ⚠️ THE MODAL'S TWO EVENTS REUSE THE OPEN-TIME `extraInfo`. Upstream computes it once in
 * `onCreateInATS` and closes over it, so "ModalCreateClicked" carries the context of the moment the
 * modal OPENED, not of the press.
 */
export interface CreateInAtsTracking {
  /** `ClientContactCreateInATSClicked` (GET `/track/l`, label `'' + alertId`); returns its extraInfo. */
  createInAtsClicked: (contact: CreateInAtsContact) => string
  /** `ClientContactCreateInATSModalCreateClicked` / `…ModalUpdateClicked`, with that extraInfo. */
  modalSubmitClicked: (contact: CreateInAtsContact, context: AddAtsClientContactContext, extraInfo: string) => void
  /**
   * `openInAts(dto, …, 'ViewCreatedClientContactInAtsClicked')`'s tracking half — the single-action
   * POST, value `null` (the dto has no id), extraInfo built over `{ externalId }`.
   */
  viewCreatedInAts: (created: { externalId: string | null | undefined; name: string | null | undefined }) => void
}

export interface CreateInAtsApi {
  createInAts: (params: AddAtsClientContactParams) => Promise<ClientContactAsyncMessageResponse | null>
  getLastAddedAtsClientTracking: (clientContactId: number) => Promise<AddAtsClientContactTracking | null>
}

const defaultApi: CreateInAtsApi = {
  createInAts: createInAtsApi,
  getLastAddedAtsClientTracking: getLastAddedAtsClientTrackingApi,
}

export interface UseCreateClientContactInAtsOptions {
  contact: CreateInAtsContact | undefined
  /** The snippet's `alertId` binding: the review's side view passes it, the standalone profile not. */
  alertId: number | undefined
  atsName: string | undefined
  /** `getAtsLabel($window)` — every string here that NAMES the ATS. Defaults to `atsName`. */
  atsLabel?: string | undefined
  updateSupported: boolean
  createNewCompanyDisabled: boolean
  updateContactDetailsEnabled: boolean
  requestContactDetailsEnabled?: boolean
  contactDetailsCtaLabel: string
  /** The screen's container; `ready` settles once its pending list is loaded. */
  asyncContainer: ClientContactAsyncContainer | undefined
  asyncContainerReady: Promise<unknown> | undefined
  viewMore: boolean
  /**
   * `toggleViewMore()` — `onCreateInATS` opens More first when it is closed, and THEN shows the form.
   *
   * ⚠️ THE FORM HAS TO SURVIVE THAT NAVIGATION, AND IN THIS PORT THE PROFILE DOES NOT. Upstream's
   * snippet lives on the parent state and outlives the `/more` transition; here the extension
   * guard re-resolves on every location and unmounts the outlet while it does, so the profile —
   * and any state it held — is rebuilt on `/more`. The intent is therefore handed to the
   * navigation (location state) and comes back as `openIntent` on the other side. Answer `false`
   * when no navigation happened (ui-router ignores a transition to the current state) and the
   * form opens here instead.
   */
  onToggleViewMore: (intent: CreateInAtsOpenIntent) => boolean
  /** The intent the More navigation carried, until `onIntentConsumed` is called for it. */
  openIntent?: CreateInAtsOpenIntent | undefined
  onIntentConsumed?: () => void
  tracking?: CreateInAtsTracking
  /**
   * `clientContactActionsService.openInAts(dto, …)`'s OPENING half. ⚠️ Nothing in this port opens
   * a contact in the ATS yet (the snippet's own "View in ATS" is inert too) — it needs the shell's
   * `openEntityInAts`, Phase 6.3/6.4. Tracking is sent either way.
   */
  onOpenCreatedInAts?: (created: { externalId: string | null | undefined; name: string | null | undefined }) => void
  /**
   * `notifyMe`'s second half: `snippetState !== ClientContactProfile` → `closeSnippetWindow()`.
   * The standalone profile passes nothing; the review's side view returns to the review.
   */
  onNotifyMeCloseSnippet?: () => void
  api?: CreateInAtsApi
  /** Passed through to the form (tests inject the employer search and the validation). */
  modalOverrides?: Pick<CreateClientContactModalProps, 'searchEmployers' | 'validate'>
}

export interface CreateClientContactInAts {
  /** `ClientContactSnippet`'s three props for the button. */
  buttonDisabled: boolean
  buttonTooltip: string | undefined
  onCreateInAts: () => void
  /** Both modals, to render beside the snippet. */
  modals: ReactElement | null
  /** Exposed for tests and for a future GET_LATEST_DETAILS consumer. */
  state: CreateInAtsState
}

/** What the form needs to open on the far side of the More navigation. */
export interface CreateInAtsOpenIntent {
  /** The click's extraInfo, which the modal's two events reuse. */
  extraInfo: string
}

interface WaitingModal {
  context: AddAtsClientContactContext
  newContactName: string | null | undefined
}

export function useCreateClientContactInAts({
  contact,
  alertId,
  atsName,
  atsLabel = atsName,
  updateSupported,
  createNewCompanyDisabled,
  updateContactDetailsEnabled,
  requestContactDetailsEnabled,
  contactDetailsCtaLabel,
  asyncContainer,
  asyncContainerReady,
  viewMore,
  onToggleViewMore,
  openIntent,
  onIntentConsumed,
  tracking,
  onOpenCreatedInAts,
  onNotifyMeCloseSnippet,
  api = defaultApi,
  modalOverrides,
}: UseCreateClientContactInAtsOptions): CreateClientContactInAts {
  const [lastCreated, setLastCreated] = useState<number | undefined>(undefined)
  const [createPending, setCreatePending] = useState(false)
  const [updatePending, setUpdatePending] = useState(false)
  const [result, setResult] = useState<ClientContactAddAtsCompletedOperationResult | null | undefined>(undefined)
  /** The open form, with the extraInfo `onCreateInATS` computed for it. */
  const [form, setForm] = useState<{ extraInfo: string } | undefined>(undefined)
  const [waiting, setWaiting] = useState<WaitingModal | undefined>(undefined)

  /** `asyncEventCallbackCanceler` — the latest subscription, cancelled on destroy. */
  const canceller = useRef<(() => void) | undefined>(undefined)

  /** `handleCompletedOperationByType`. GET_LATEST_DETAILS has no consumer in this port yet. */
  const onCompleted = useCallback((operation: ClientContactCompletedOperation): void => {
    switch (operation.operationType) {
      case ClientContactAsyncOperationType.CREATE_CLIENT_CONTACT_IN_ATS: {
        // `onClientContactAddedPendingOperationCb`.
        setCreatePending(false)
        const added = operation.result as ClientContactAddAtsCompletedOperationResult | null
        setResult(added)
        if (!added?.successful) return
        setLastCreated(Date.now())
        return
      }
      case ClientContactAsyncOperationType.UPDATE_CLIENT_CONTACT_IN_ATS:
        // `onClientContactUpdatedPendingOperationCb` — the flag, and nothing else.
        setUpdatePending(false)
        return
      default:
        return
    }
  }, [])

  const contactId = contact?.id
  // `$onInit`'s tail: `await initLastCreatedClientContact(); handlePendingOperationsOnInit()`.
  useEffect(() => {
    if (contactId === undefined) return undefined
    let live = true
    setLastCreated(undefined)
    setCreatePending(false)
    setUpdatePending(false)
    void (async () => {
      // ⚠️ A REJECTION HERE ENDS `$onInit` UPSTREAM, so the pending operations are not picked
      // up either; kept, since it is the read that failed and not this port.
      const trackingRow = await api.getLastAddedAtsClientTracking(contactId)
      if (!live) return
      if (trackingRow?.creationDate) setLastCreated(trackingRow.creationDate)
      if (asyncContainer === undefined) return
      await asyncContainerReady
      if (!live) return
      const relevant = asyncContainer
        .getClientContactPendingOperations()
        .filter((operation) => operation.clientContactId === contactId)
      if (relevant.length === 0) return
      canceller.current = asyncContainer.subscribeToAsyncEventFromSnippet(contactId, onCompleted)
      // `handlePendingOperations` — GET_LATEST_DETAILS would start the details progress here.
      for (const operation of relevant) {
        if (operation.operationType === ClientContactAsyncOperationType.CREATE_CLIENT_CONTACT_IN_ATS) setCreatePending(true)
        if (operation.operationType === ClientContactAsyncOperationType.UPDATE_CLIENT_CONTACT_IN_ATS) setUpdatePending(true)
      }
    })().catch(() => undefined)
    return () => {
      live = false
      canceller.current?.()
      canceller.current = undefined
    }
  }, [contactId, asyncContainer, asyncContainerReady, api, onCompleted])

  const state: CreateInAtsState = {
    withCompaniesAddedByWoo: contact === undefined ? false : companiesAddedByWoo(contact).length > 0,
    updateSupported,
    lastCreated,
    createPending,
    updatePending,
  }

  /** `onCreateInATS` — track, open More if it is closed, then the form. */
  const onCreateInAts = (): void => {
    if (contact === undefined) return
    const extraInfo = tracking?.createInAtsClicked(contact) ?? ''
    if (!viewMore && onToggleViewMore({ extraInfo })) return
    setForm({ extraInfo })
  }

  // The far side of that navigation: open the form once the contact is back.
  const hasContact = contact !== undefined
  useEffect(() => {
    if (openIntent === undefined || !hasContact) return
    onIntentConsumed?.()
    setForm({ extraInfo: openIntent.extraInfo })
  }, [openIntent, hasContact, onIntentConsumed])

  /** `onCreateInAtsFormSent` / `onUpdateInAtsFormSent` — the write, then the pending state and the wait. */
  const sendForm = async (context: AddAtsClientContactContext, params: AddAtsClientContactParams): Promise<void> => {
    if (contact === undefined) return
    let response: ClientContactAsyncMessageResponse | null
    try {
      response = await api.createInAts(params)
    } catch {
      // Unhandled upstream: the request-failure alert reports it, and nothing becomes pending.
      return
    }
    if (!response) return
    const create = context === AddAtsClientContactContext.CREATE
    if (create) setCreatePending(true)
    else setUpdatePending(true)
    // `logPendingOperation`.
    if (asyncContainer !== undefined) {
      canceller.current = asyncContainer.onInitiateAsyncEventFromSnippet(
        {
          id: response.messageId,
          clientContactId: contact.id,
          operationType: create
            ? ClientContactAsyncOperationType.CREATE_CLIENT_CONTACT_IN_ATS
            : ClientContactAsyncOperationType.UPDATE_CLIENT_CONTACT_IN_ATS,
        },
        onCompleted,
      )
    }
    setWaiting({ context, newContactName: params.name })
  }

  const modals =
    contact === undefined ? null : (
      <>
        {form === undefined ? null : (
          <CreateClientContactModal
            contact={contact}
            alertId={alertId}
            atsName={atsName}
            atsLabel={atsLabel}
            state={state}
            createNewCompanyDisabled={createNewCompanyDisabled}
            updateContactDetailsEnabled={updateContactDetailsEnabled}
            {...(requestContactDetailsEnabled === undefined ? {} : { requestContactDetailsEnabled })}
            contactDetailsCtaLabel={contactDetailsCtaLabel}
            {...modalOverrides}
            onSubmit={(context, params) => {
              tracking?.modalSubmitClicked(contact, context, form.extraInfo)
              void sendForm(context, params)
              setForm(undefined)
            }}
            onClose={() => {
              setForm(undefined)
            }}
          />
        )}
        {waiting === undefined ? null : (
          <CreateClientContactWaitingModal
            context={waiting.context}
            newContactName={waiting.newContactName}
            result={result}
            goToAtsCopy={createdContactGoToAtsCopy(atsLabel)}
            onNotifyMe={() => {
              setWaiting(undefined)
              onNotifyMeCloseSnippet?.()
            }}
            onOpenInAts={() => {
              // `onOpenCreatedClientContactInAts(clientContactAddToAtsResult.result, newContactName)`.
              const created = { externalId: result?.result, name: waiting.newContactName }
              tracking?.viewCreatedInAts(created)
              onOpenCreatedInAts?.(created)
              setWaiting(undefined)
            }}
            onClose={() => {
              setWaiting(undefined)
            }}
          />
        )}
      </>
    )

  return {
    buttonDisabled: isCreateInAtsButtonDisabled(state),
    buttonTooltip: contact === undefined ? undefined : disabledCreateInAtsTooltip(state, contact, atsLabel),
    onCreateInAts,
    modals,
    state,
  }
}
